import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Box, HStack, Icon, Input, InputGroup, Spinner, Text, VStack} from '@chakra-ui/react';
import {Plus, Search, UserMinus, X} from 'lucide-react';
import {Button} from '@/components';
import {useApi} from '@/uses/useApi';
import {useLanguage} from '@/contexts/languageContext';
import {toaster} from '@/components/ui/toaster';

const MAX_SUGGESTIONS = 8;
const MIN_SEARCH_LENGTH = parseInt(process.env.REACT_APP_MIN_SEARCH_LENGTH) || 3;
const SEARCH_TIMEOUT = parseInt(process.env.REACT_APP_SEARCH_TIMEOUT) || 500;

const getUserName = (user) => `${user?.firstname ?? ''} ${user?.lastname ?? ''}`.trim();

/**
 * Gestion des participants exclus des résultats d'un sondage.
 *
 * Les réponses n'exposent jamais leur auteur : on sélectionne donc des utilisateurs
 * (recherche MeiliSearch sur `/user`) et c'est l'API qui écarte leurs réponses du
 * calcul. `onChange` laisse l'écran parent recharger les résultats filtrés.
 */
export const PollExclusions = ({pollId, excludedUsers = [], onChange}) => {
    const api = useApi();
    const {language, t} = useLanguage();

    const [searchText, setSearchText] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [searching, setSearching] = useState(false);
    const [saving, setSaving] = useState(false);

    const excludedIds = useMemo(() => excludedUsers.map(({id}) => id), [excludedUsers]);

    const searchTimeout = useRef();
    useEffect(() => {
        clearTimeout(searchTimeout.current);
        if (searchText.trim().length < MIN_SEARCH_LENGTH) {
            setSuggestions([]);
            setSearching(false);
            return;
        }
        setSearching(true);
        searchTimeout.current = setTimeout(() => {
            api.ApiService.get('user', {
                search: searchText.trim(),
                limit: MAX_SUGGESTIONS,
                locale: language,
            })
                .then((res) => {
                    if (res?.data?.type === 'success') {
                        setSuggestions(res?.data?.value?.hits ?? []);
                    }
                })
                .catch((e) => console.log(e?.response?.data))
                .finally(() => setSearching(false));
        }, SEARCH_TIMEOUT);

        return () => clearTimeout(searchTimeout.current);
    }, [searchText, language]);

    /** L'exclusion est enregistrée immédiatement pour que les résultats affichés restent cohérents */
    const save = useCallback((excludedUserIds) => {
        setSaving(true);
        return api.ApiService.rawPut(`/poll/${pollId}/excluded-users`, {excludedUserIds})
            .then((res) => {
                if (res?.data?.type === 'success') {
                    onChange?.();
                }
            })
            .catch((e) => console.log(e?.response?.data))
            .finally(() => setSaving(false));
    }, [pollId, onChange]);

    const exclude = useCallback((user) => {
        if (excludedIds.includes(user.id)) return;
        save([...excludedIds, user.id]).then(() => {
            setSearchText('');
            setSuggestions([]);
            toaster.create({title: t('polls.participantExcluded', {name: getUserName(user)}), type: 'success'});
        });
    }, [excludedIds, save, t]);

    const include = useCallback((user) => {
        save(excludedIds.filter((id) => id !== user.id)).then(() => {
            toaster.create({title: t('polls.participantIncluded', {name: getUserName(user)}), type: 'success'});
        });
    }, [excludedIds, save, t]);

    const visibleSuggestions = useMemo(
        () => suggestions.filter(({id}) => !excludedIds.includes(id)),
        [suggestions, excludedIds]
    );

    const UserRow = (user, type) => (
        <HStack
            key={`excluded-${type}-${user.id}`}
            w="100%"
            justify="space-between"
            gap={3}
            bg="gray.50"
            borderRadius="md"
            px={3}
            py={2}
        >
            <VStack align="start" gap={0} overflow="hidden">
                <Text fontSize="sm" fontWeight="semibold" isTruncated>{getUserName(user)}</Text>
                <Text fontSize="xs" color="gray.500" isTruncated>{user?.email}</Text>
            </VStack>
            <Button
                visual="icon"
                disabled={saving}
                title={type === 'add' ? t('polls.excludeParticipant') : t('polls.includeParticipant')}
                onClick={() => (type === 'add' ? exclude(user) : include(user))}
            >
                <Icon as={type === 'add' ? Plus : X} boxSize={4}/>
            </Button>
        </HStack>
    );

    return (
        <Box bg="white" borderRadius="xl" p={6} shadow="sm" border="1px solid" borderColor="gray.100" w="100%">
            <HStack gap={2} mb={1} color="secondary.500">
                <Icon as={UserMinus} boxSize={4}/>
                <Text fontSize="sm" fontWeight="semibold">{t('polls.excludedParticipants')}</Text>
                {saving && <Spinner size="xs"/>}
            </HStack>
            <Text fontSize="xs" color="gray.500" mb={4}>{t('polls.excludedParticipantsHelp')}</Text>

            <VStack align="stretch" gap={4}>
                <Box>
                    <InputGroup
                        endElement={searching ? <Spinner size="xs"/> : <Icon as={Search} boxSize={4} color="gray.400"/>}
                    >
                        <Input
                            autoComplete="off"
                            value={searchText}
                            placeholder={t('polls.searchParticipant')}
                            onChange={(e) => setSearchText(e?.target?.value ?? '')}
                        />
                    </InputGroup>
                    {searchText.trim().length >= MIN_SEARCH_LENGTH && (
                        <VStack align="stretch" gap={2} mt={2} maxH="240px" overflowY="auto">
                            {
                                visibleSuggestions.length > 0
                                    ? visibleSuggestions.map((user) => UserRow(user, 'add'))
                                    : !searching && <Text fontSize="sm" color="gray.400">{t('sentences.noItem')}</Text>
                            }
                        </VStack>
                    )}
                </Box>

                <VStack align="stretch" gap={2} maxH="240px" overflowY="auto">
                    {
                        excludedUsers.length > 0
                            ? excludedUsers.map((user) => UserRow(user, 'delete'))
                            : <Text fontSize="sm" color="gray.400">{t('polls.noExcludedParticipant')}</Text>
                    }
                </VStack>
            </VStack>
        </Box>
    );
};

export default PollExclusions;
